import { useState } from 'react';
import { KeyRound, X } from 'lucide-react';
import { staffApi } from '../../services/staffApi';
import { RubyGemMark } from '../RubyGemMark';

/**
 * "Reset two-step sign-in" for another super admin who lost their
 * authenticator. The server refuses it on your own account and alerts the
 * person + every other super admin; this button only asks for confirmation.
 */
export function ResetMfaButton({ userId, name, email, compact = false }: {
  userId: string;
  name: string;
  email: string;
  compact?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null);

  const close = () => { setOpen(false); setResult(null); };

  const confirm = async () => {
    setBusy(true);
    try {
      const r = await staffApi.security.resetMfa(userId);
      setResult({ ok: true, message: r.message });
    } catch (e: any) {
      setResult({ ok: false, message: e?.message || 'Reset failed' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className={`inline-flex items-center gap-1.5 shrink-0 rounded-lg text-xs font-medium bg-stone-100 text-stone-700 border border-stone-300 hover:bg-stone-200 transition-colors ${compact ? 'px-2.5 py-1.5' : 'px-2.5 py-2 min-h-[44px]'}`}
        title="Reset two-step sign-in (lost authenticator)"
      >
        <KeyRound size={14} />
        <span className={compact ? '' : 'hidden sm:inline'}>Reset 2-step</span>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={(e) => { e.stopPropagation(); if (!busy) close(); }}
        >
          <div className="w-full max-w-md bg-white rounded-xl shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between bg-black text-white px-5 py-3 rounded-t-xl">
              <h3 className="font-semibold">Reset two-step sign-in</h3>
              <button type="button" onClick={close} disabled={busy} className="w-11 h-11 -mr-3 flex items-center justify-center text-stone-300 hover:text-white">
                <X size={18} />
              </button>
            </div>
            <div className="h-[3px] bg-gradient-to-r from-[#DC143C] to-[#9F1239]" />
            <div className="p-5 space-y-3 text-sm text-stone-700">
              {!result ? (
                <>
                  <p>
                    Use this only when <strong>{name}</strong> ({email}) has lost their authenticator app
                    and has no recovery codes. Confirm it is really them first, for example by phone.
                  </p>
                  <ul className="list-disc pl-5 space-y-1 text-stone-600">
                    <li>Their next sign-in asks for their password plus a one-time code sent to their email.</li>
                    <li>They must then set up a new authenticator and get new recovery codes.</li>
                    <li>The reset expires after 24 hours if unused.</li>
                    <li>They and every other super admin are emailed, and it is logged.</li>
                  </ul>
                  <div className="flex justify-end gap-2 pt-2">
                    <button type="button" onClick={close} className="min-h-[44px] px-4 rounded-lg border border-stone-300 text-stone-700 hover:bg-stone-50">
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={confirm}
                      disabled={busy}
                      className="min-h-[44px] px-4 rounded-lg bg-[#DC143C] hover:bg-[#9F1239] text-white font-medium inline-flex items-center gap-2 disabled:opacity-60"
                    >
                      {busy && <RubyGemMark size={16} drawing color="currentColor" />}
                      Reset two-step sign-in
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p className={result.ok ? 'text-emerald-700' : 'text-red-600'}>{result.message}</p>
                  <div className="flex justify-end pt-2">
                    <button type="button" onClick={close} className="min-h-[44px] px-4 rounded-lg border border-stone-300 text-stone-700 hover:bg-stone-50">
                      Close
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
